import type { FormChatState } from "../repositories/formChat.repository";
import type {
  CreateRitualBathPayload,
  RitualBathIngredient,
  RitualBathScheduleInput,
  RitualBathStep,
  UpdateRitualBathPayload,
} from "../types/ritualBath.types";

const SCHEDULE_FIELDS = [
  "scheduleOnCalendar",
  "scheduleDate",
  "scheduleStartTime",
  "scheduleEndTime",
];

const optionalText = (value: string | null | undefined) => value?.trim() ?? "";

const toIngredients = (state: FormChatState): RitualBathIngredient[] =>
  (state.bathIngredients ?? [])
    .filter((ingredient) => ingredient.name?.trim())
    .map((ingredient) => ({
      name: ingredient.name.trim(),
      ...(ingredient.quantity ? { quantity: String(ingredient.quantity) } : {}),
      ...(ingredient.unit ? { unit: ingredient.unit } : {}),
      ...(ingredient.isOptional ? { isOptional: true } : {}),
      ...(ingredient.notes ? { notes: ingredient.notes } : {}),
    }));

const toSteps = (state: FormChatState): RitualBathStep[] =>
  (state.bathSteps ?? [])
    .filter((step) => step.text?.trim())
    .map((step, index) => ({
      order: index + 1,
      text: step.text.trim(),
      type: step.type,
      ...(step.icon ? { icon: step.icon } : {}),
    }));

const toSchedule = (state: FormChatState): RitualBathScheduleInput | undefined => {
  if (!state.scheduleOnCalendar || !state.scheduleDate) return undefined;
  return {
    date: state.scheduleDate,
    ...(state.scheduleStartTime ? { startTime: state.scheduleStartTime } : {}),
    ...(state.scheduleEndTime ? { endTime: state.scheduleEndTime } : {}),
  };
};

/** Same body CreateRitualBath posts to /ritual-baths; null when the chat state is incomplete. */
export const buildRitualBathCreatePayload = (
  state: FormChatState,
  owner: { terreiroId?: string; mediumId?: string },
): CreateRitualBathPayload | null => {
  const title = state.bathTitle?.trim();
  if (!title || !state.bathCategoryId) return null;
  if (!owner.terreiroId && !owner.mediumId) return null;
  const schedule = toSchedule(state);
  return {
    title,
    description: optionalText(state.bathDescription),
    introduction: optionalText(state.bathIntroduction),
    source: optionalText(state.bathSource),
    categoryId: state.bathCategoryId,
    ...(owner.terreiroId
      ? { origin: "TERREIRO" as const, terreiroId: owner.terreiroId }
      : { origin: "MEDIUM" as const, mediumId: owner.mediumId }),
    status: state.bathStatus ?? "ACTIVE",
    ingredients: toIngredients(state),
    steps: toSteps(state),
    ...(schedule ? { schedule } : {}),
  };
};

/**
 * PUT /ritual-baths/:id body with only the fields the chat changed, so lists
 * and the schedule are never rewritten when untouched. Null when nothing changed.
 */
export const buildRitualBathUpdatePayload = (
  state: FormChatState,
  dirtyFields: string[],
): Partial<UpdateRitualBathPayload> | null => {
  const dirty = new Set(dirtyFields);
  const payload: Partial<UpdateRitualBathPayload> = {};
  if (dirty.has("bathTitle") && state.bathTitle?.trim()) {
    payload.title = state.bathTitle.trim();
  }
  if (dirty.has("bathCategoryId") && state.bathCategoryId) {
    payload.categoryId = state.bathCategoryId;
  }
  if (dirty.has("bathDescription")) {
    payload.description = optionalText(state.bathDescription);
  }
  if (dirty.has("bathIntroduction")) {
    payload.introduction = optionalText(state.bathIntroduction);
  }
  if (dirty.has("bathSource")) payload.source = optionalText(state.bathSource);
  if (dirty.has("bathStatus") && state.bathStatus) payload.status = state.bathStatus;
  if (dirty.has("bathIngredients")) payload.ingredients = toIngredients(state);
  if (dirty.has("bathSteps")) payload.steps = toSteps(state);
  if (SCHEDULE_FIELDS.some((field) => dirty.has(field))) {
    const schedule = toSchedule(state);
    if (schedule) payload.schedule = schedule;
  }
  return Object.keys(payload).length > 0 ? payload : null;
};
